/**
 * EIN PRODUKT ANLEGEN -- GEFUEHRT, IN SIEBEN SCHRITTEN.
 *
 * WARUM NICHT MEHR EIN FORMULAR
 *
 * V1 war ein Dialog mit vier Feldern und einer guten Begruendung: wer einen
 * Artikel anlegt, kennt Nummer und Familie, alles andere kommt spaeter. Das
 * stimmt weiterhin -- aber es beantwortete die falsche Frage. Das Problem war
 * nie die Zahl der Felder, sondern dass der Anlegende ALLEIN entscheiden
 * musste, in welche Familie ein Produkt gehoert, ob es das schon gibt und
 * welche Nummer dazu passt. Vier Felder, die nichts zeigen, sind schwerer
 * auszufuellen als Schritte, die etwas zeigen.
 *
 * WAS DIE SICHTPRUEFUNG VOM 11.09.2026 ERGEBEN HAT
 *
 * Arne wollte aus `HB200-U4KD2D-85-CRI` eine Ausfuehrung mit CRI 80 machen.
 * Heraus kam `HB200-U4KD2D-85` -- ein Artikel in derselben Familie, ohne
 * Bezeichnung, ohne Merkmale, ohne Bezug zum Ausgangsprodukt. Drei Befunde:
 *
 *   1. Die Wahl des Variantenmerkmals bot nur den Abstrahlwinkel an. Das war
 *      RICHTIG: Schritt „Variantenparameter" liest den Variantenraum des
 *      Ausgangsprodukts, und `HB200-U4KD2D-85-CRI` fuehrt dort genau eine
 *      Achse -- `beam_angle` mit drei Ausfuehrungen. CRI ist an sieben
 *      ANDEREN Artikeln als Achse hinterlegt, an diesem nicht; die
 *      Farbtemperatur an keinem einzigen. Der Assistent hat nichts
 *      verschwiegen -- er hat gezeigt, was die Quelle ueber dieses Produkt
 *      sagt.
 *   2. Es entstand ein leerer Artikel. Auch das war die gebaute Absicht:
 *      Ausgangsprodukt und Parameter sind ANSCHAUUNG, sie werden nicht
 *      uebernommen. Der Grund steht unten unter VERERBUNG.
 *   3. Eine neue Produktfamilie liess sich nicht anlegen. Das war eine echte
 *      Sackgasse -- und ist behoben (Schritt 2).
 *
 * DIE SIEBEN SCHRITTE
 *
 *   1 Hauptkategorie      grenzt die Familienliste von ueber vierzig auf
 *                         wenige ein -- die eigentliche Entscheidungshilfe
 *   2 Familie             bestehende waehlen ODER eine neue anlegen
 *   3 Anlageart           Variante eines Produkts -- oder ein Produkt ohne
 *                         Vorbild. Die drei Faelle bleiben unterscheidbar.
 *   4 Ausgangsprodukt     was es in dieser Familie schon gibt
 *   5 Variantenparameter  in welchen Ausfuehrungen es das gibt
 *   6 Identitaet          Nummer, Bezeichnung, Rollen
 *   7 Anlagevorschau      was gleich entsteht -- und was ausdruecklich nicht
 *
 * DIE DREI FAELLE
 *
 *   A  Variante eines bestehenden Produkts -- Familie und Vorbild vorhanden
 *   B  Neues Produkt in bestehender Familie -- kein geeignetes Vorbild
 *   C  Erstes Produkt einer neuen Familie -- die Familie entsteht hier
 *
 * Sie werden NICHT durch denselben Mechanismus gepresst: C legt zuerst die
 * Familie an, B ueberspringt Vorbild und Parameter, nur A zeigt beide.
 *
 * VERERBUNG -- UND WARUM SIE HIER NICHT STATTFINDET
 *
 * Der neue Artikel entsteht aus Nummer, Familie, Bezeichnung und Rollen.
 * Merkmale, Texte, Supplier-Beziehungen, Preise, Verpackung und
 * Zertifizierungen werden NICHT kopiert. Das ist keine Bequemlichkeit,
 * sondern die Grenze des Herkunftsmodells:
 *
 *   `product_properties` laesst genau drei Herkuenfte zu -- ein
 *   Extraktionssegment aus dem Quelltext DIESES Artikels, eine Zeile im
 *   Hauptbuch mit Datei, Blatt, Zeile und Spalte, oder „erfasst" durch einen
 *   Menschen (`pp_extraction_method_chk`, `pp_enrichment_method_chk`,
 *   `pp_one_provenance_chk`). „Uebernommen aus Artikel X" ist keine davon.
 *   Ein kopierter Wert muesste also entweder das Quellsegment eines FREMDEN
 *   Artikels fuer sich beanspruchen oder behaupten, jemand habe ihn
 *   eingetippt. Beides waere eine Herkunftsluege in einem Modell, dessen
 *   ganzer Zweck die Herkunft ist.
 *
 * Der kanonische Ort fuer „diese SKU ist Ausfuehrung X von jenem Produkt"
 * ist die Modellebene aus 0010: `product_models` mit `model_dimensions` und
 * `model_variants`, wo `article_id` der Materialisierungsschalter ist. Sie
 * existiert, ist aber leer -- welche Artikel eine technische Plattform
 * bilden, ist eine fachliche Setzung, die niemand getroffen hat.
 *
 * Schritt 7 sagt darum ausdruecklich, was NICHT entsteht. Ein Assistent, der
 * das verschweigt, erzeugt genau die Ueberraschung vom 11.09.2026.
 *
 * WAS TROTZDEM GEWONNEN IST
 *
 * Wer sieht, dass es die Leuchte in 3000 K und 4000 K schon gibt, vergibt
 * eine passende Nummer statt einer beliebigen -- und legt sie nicht zum
 * zweiten Mal an. Die Bezeichnung des Ausgangsprodukts steht als VORSCHLAG
 * bereit, statt leer zu bleiben; genau ihr Fehlen hat `HB200-U4KD2D-85`
 * unbrauchbar gemacht. Und eine neue Baureihe fuehrt nicht mehr aus dem
 * Assistenten hinaus.
 */
import { useEffect, useMemo, useState } from "react";
import type {
  ArticleDetail, ArticleListItem, ArticleTrashEntry, CategoryItem, CctValue, FamilyItem, MetaResponse, ModelDimension,
  ProductModelDetail, ProductModelListItem, PropertyDefinition, VariantDimension, VariantOptionValue,
} from "@homebase/shared";
import { api, ApiError } from "../lib/api.ts";
import { useI18n, type I18n } from "../i18n/index.tsx";
import { errorText } from "../i18n/errors.ts";
import { categoryLabel } from "../i18n/terminology.ts";
import { displayValue, unitSuffix } from "./ProductProperties.tsx";
import { ModelDimensions } from "./ModelVariants.tsx";
import "./ArticleCreate.css";
import type { AusfuehrungsKontext } from "./ModelAssign.tsx";

/**
 * Die Schritte als Zahlen, nicht als Namen.
 *
 * Vorwaerts geht es nur, wenn der jeweilige Schritt beantwortet ist; zurueck
 * immer. Ein Assistent, der einen gefangen haelt, ist ein Formular mit
 * schlechterer Laune.
 */
const SCHRITTE = [1, 2, 3, 4, 5, 6, 7] as const;
type Schritt = (typeof SCHRITTE)[number];

/**
 * Die Anlageart -- die Weiche zwischen den drei Faellen.
 *
 * Sie wird GEFRAGT und nicht geraten. Aus „es gibt kein Ausgangsprodukt"
 * zu schliessen, der Benutzer wolle keines, waere dieselbe Art stiller
 * Annahme, die den leeren Artikel erzeugt hat.
 */
type Anlageart = "variante" | "ohne";

export function ArticleCreate({ meta, onClose, onCreated, onOpenTrash, mayWriteModels = false, ausfuehrung = null }: {
  meta: MetaResponse;
  /**
   * DER ASSISTENT AUS EINER AUSFUEHRUNG HERAUS (Produktmodelle 002A).
   *
   * Kategorie, Familie, Produktmodell und die Werte der Ausfuehrung stehen
   * dann schon fest und werden nicht erneut abgefragt; der Assistent beginnt
   * bei der Artikelnummer. Beim Anlegen wird die BESTEHENDE Ausfuehrung mit
   * dem neuen Artikel verbunden, statt eine zweite gleiche anzulegen.
   * Aendert jemand eine Achse, gilt wieder der gewohnte Weg.
   */
  ausfuehrung?: AusfuehrungsKontext | null;
  onClose: () => void;
  /**
   * Darf dieser Nutzer Produktmodelle schreiben?
   *
   * Der Workflow ist Wegweisung, keine Rechteerweiterung: wer ein Modell
   * nicht anlegen darf, darf es auch von hier aus nicht. Ohne das Recht
   * bleibt der Hinweis, dass ein Modell fehlt -- nur ohne Anlageweg.
   */
  mayWriteModels?: boolean;
  /** Der neue Artikel wird unmittelbar geoeffnet -- Anlegen ist ein Anfang. */
  onCreated: (id: number) => void;
  /**
   * Den Papierkorb oeffnen.
   *
   * Gebraucht genau dann, wenn die eingegebene Nummer dort liegt: eine
   * Sperre, die sagt „sieh im Papierkorb nach", und keinen Weg dorthin
   * anbietet, ist eine Sackgasse.
   */
  onOpenTrash?: () => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;

  const [schritt, setSchritt] = useState<Schritt>(ausfuehrung ? 6 : 1);
  const [categoryId, setCategoryId] = useState(ausfuehrung ? String(ausfuehrung.categoryId) : "");
  const [familyId, setFamilyId] = useState(ausfuehrung ? String(ausfuehrung.familyId) : "");
  const [anlageart, setAnlageart] = useState<Anlageart | null>(ausfuehrung ? "ohne" : null);
  const [ausgangsId, setAusgangsId] = useState<number | null>(null);
  /* Eine in DIESEM Durchlauf angelegte Familie -- daran haengt Fall C. */
  const [neueFamilie, setNeueFamilie] = useState<FamilyItem | null>(null);
  const [bezeichnungBeruehrt, setBezeichnungBeruehrt] = useState(false);
  const [parameter, setParameter] = useState<Record<number, number>>({});
  const [nummer, setNummer] = useState("");
  const [bezeichnung, setBezeichnung] = useState("");
  const [rollen, setRollen] = useState<readonly string[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  /*
   * EINE ENDGUELTIG GELOESCHTE NUMMER WIRD NICHT STILL WIEDERVERWENDET.
   *
   * Der Server fragt nach (`article_number_previously_deleted`), wenn zu der
   * Nummer ein Grabstein steht. Bis hierher endete der Assistent dort mit
   * einer Fehlermeldung und ohne Weg. Jetzt steht die Frage im Formular --
   * und die Antwort ist eine bewusste Zustimmung, die im Protokoll landet.
   */
  const [nummerFrei, setNummerFrei] = useState<string | null>(null);
  const [wiederverwenden, setWiederverwenden] = useState(false);

  /* Angeboten wird, was aktiv ist -- archivierte Ordnung traegt keine
     Neuanlage mehr (Lifecycle 001). Bestehende Artikel zeigen ihre
     Kategorie und Familie unveraendert weiter. */
  const kategorien = useMemo(
    () => meta.categories.filter((c) => c.archived !== true && c.trashed !== true).sort((a, b) =>
      categoryLabel(i18n, a.code, a.name).localeCompare(
        categoryLabel(i18n, b.code, b.name), i18n.lang)),
    [meta.categories, i18n]);

  /*
   * Familien der gewaehlten Kategorie -- alphabetisch.
   *
   * Die Reihenfolge der Nachschlagedaten folgt der Kategorie, nicht dem
   * Suchverhalten; wer eine Familie sucht, sucht sie nach Namen.
   */
  const familien = useMemo(
    () => [...meta.families.filter((f) => f.archived !== true && f.trashed !== true),
           ...(neueFamilie ? [neueFamilie] : [])]
      .filter((f) => categoryId === "" || String(f.categoryId) === categoryId)
      .sort((a, b) => a.name.localeCompare(b.name, i18n.lang)),
    [meta.families, neueFamilie, categoryId, i18n.lang]);
  const gewaehlteFamilie = familien.find((f) => String(f.id) === familyId) ?? null;
  const kategorie = gewaehlteFamilie
    ? meta.categories.find((c) => c.id === gewaehlteFamilie.categoryId) ?? null
    : null;

  const { artikel, ladeArtikel } = useFamilienartikel(familyId);
  const ausgang = artikel.find((a) => a.id === ausgangsId) ?? null;
  const { achsen, ladeAchsen } = useVariantenraum(ausgangsId);
  /*
   * DAS PRODUKTMODELL DES AUSGANGSPRODUKTS.
   *
   * Traegt das Vorbild ein Modell, ist die Variantenbildung eine andere
   * Sache als das blosse Zeigen: die Achsen kommen dann aus dem MODELL
   * (`model_dimensions`), die Ausgangswerte aus der Variante des Vorbilds,
   * und die neue SKU wird als weitere Variante desselben Modells
   * materialisiert -- ohne einen einzigen Merkmalswert zu kopieren.
   *
   * Ohne Modell bleibt alles wie bisher: `article_variant_options` zeigt,
   * in welchen Ausfuehrungen es das Produkt gibt, und mehr wird nicht
   * behauptet.
   */
  /*
   * DAS MODELL KOMMT AUS DER FAMILIE, NICHT NUR AUS DEM VORBILD.
   *
   * Bis zum 12.09.2026 bezog der Assistent das Produktmodell ausschliesslich
   * aus dem gewaehlten Ausgangsprodukt. Das trug, solange jede SKU einer
   * Plattform bereits zugeordnet war -- und liess den Weg
   * „Familie -> Modell -> Ausfuehrung -> SKU" nicht zu: wer das erste
   * Produkt einer Plattform anlegt, hat kein Vorbild und bekam deshalb
   * keine Modellachsen.
   *
   * Jetzt fuehrt die FAMILIE zu ihren Modellen, und das Modell fuehrt zu
   * den Achsen. Das Vorbild bestimmt weiterhin die Vorbelegung -- es ist
   * die genaueste verfuegbare Auskunft --, aber es ist nicht mehr die
   * einzige Quelle.
   */
  const { modelle, ladeModelle } = useFamilienmodelle(familyId);
  const [modellWunsch, setModellWunsch] = useState<number | null>(null);
  const { basis, ladeBasis } = useBasis(ausgangsId);
  /* Im Durchlauf angelegte Modelle stehen noch nicht in der Familienliste
     des Servers -- sie werden hier dazugehalten, bis sie es tun. */
  const [neueModelle, setNeueModelle] = useState<readonly ProductModelListItem[]>([]);
  const modelleAlle = useMemo(
    () => [...modelle, ...neueModelle.filter((n) => !modelle.some((m) => m.id === n.id))],
    [modelle, neueModelle]);
  /*
   * Vorbelegung, in dieser Reihenfolge: das Modell des Vorbilds, sonst das
   * einzige Modell der Familie. Bei mehreren ohne Vorbild waehlt ein
   * Mensch -- eine Vermutung waere hier eine Behauptung ueber die
   * technische Plattform.
   */
  useEffect(() => {
    if (ausfuehrung) { setModellWunsch(ausfuehrung.modelId); return; }
    const ausVorbild = basis?.productModel?.id ?? null;
    if (ausVorbild !== null) { setModellWunsch(ausVorbild); return; }
    setModellWunsch(modelleAlle.length === 1 ? modelleAlle[0]!.id : null);
  }, [basis?.productModel?.id, modelleAlle]);
  const { modell, ladeModell, setzeModell } = useModellDetail(modellWunsch);
  /*
   * DIE FACHLICHE REIHENFOLGE DER ACHSEN.
   *
   * Am Modell stehen sie in der Reihenfolge, in der sie gesetzt wurden --
   * eine Herkunftsangabe, keine Lesereihenfolge. Wer eine Ausfuehrung
   * waehlt, denkt zuerst an die Lichtfarbe, dann an die Qualitaet des
   * Lichts, dann an die Optik. Genau so stehen sie hier; alles Weitere
   * folgt der Modellreihenfolge.
   */
  const ACHSENFOLGE = ["cct", "cri", "beam_angle"];
  const modellAchsen = useMemo(() => [...(modell?.dimensions ?? [])].sort((a, b) => {
    const ia = ACHSENFOLGE.indexOf(a.definition), ib = ACHSENFOLGE.indexOf(b.definition);
    if (ia === -1 && ib === -1) return a.sortOrder - b.sortOrder;
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  }), [modell]);
  const [modellWahl, setModellWahl] = useState<Record<number, number>>({});

  /* Wechselt die Familie, ist ein zuvor gewaehltes Ausgangsprodukt gegenstandslos. */
  useEffect(() => { setAusgangsId(null); setParameter({}); }, [familyId]);
  useEffect(() => { setParameter({}); }, [ausgangsId]);
  /*
   * DIE AUSPRAEGUNGEN DES VORBILDS SIND DER AUSGANGSPUNKT.
   *
   * Vorbelegt wird, was das Vorbild TATSAECHLICH ist -- danach aendert der
   * Benutzer genau die eine Achse, um die es ihm geht. Ohne Vorbelegung
   * muesste er alles noch einmal zusammensuchen und koennte dabei
   * versehentlich etwas anderes bauen als gemeint.
   */
  useEffect(() => {
    if (ausfuehrung) {
      const v: Record<number, number> = {};
      for (const w of ausfuehrung.values) v[w.dimensionId] = w.valueId;
      setModellWahl(v); return;
    }
    if (basis?.variant == null) { setModellWahl({}); return; }
    const v: Record<number, number> = {};
    for (const w of basis.variant.values) v[w.dimensionId] = w.valueId;
    setModellWahl(v);
  }, [basis?.variant]);

  /*
   * DER FALL ERGIBT SICH, ER WIRD NICHT GESETZT.
   *
   * C erkennt man daran, dass die Familie in diesem Durchlauf entstanden
   * ist -- dann gibt es kein Vorbild, weil es noch nichts gibt. A und B
   * trennt die Anlageart.
   */
  const fall: "A" | "B" | "C" =
    neueFamilie !== null && String(neueFamilie.id) === familyId ? "C"
      : anlageart === "variante" ? "A" : "B";

  /*
   * DIE BEZEICHNUNG DES AUSGANGSPRODUKTS ALS VORSCHLAG.
   *
   * `HB200-U4KD2D-85` entstand ohne Bezeichnung, und genau das macht ihn
   * heute unbrauchbar (verify I13). Der Vorschlag wird VORGELEGT, nicht
   * gesetzt: er steht im Feld, ist vollstaendig aenderbar, und Schritt 7
   * sagt, dass er woertlich vom Vorbild stammt. Eine Bezeichnung
   * automatisch aus geaenderten Merkmalen umzuschreiben waere das
   * Gegenteil -- dann stuende dort ein Satz, den niemand gelesen hat.
   */
  useEffect(() => {
    if (bezeichnungBeruehrt) return;
    setBezeichnung(ausgang?.shortDescription ?? "");
  }, [ausgangsId, bezeichnungBeruehrt, ausgang?.shortDescription]);

  async function anlegen(): Promise<void> {
    setBusy(true); setError(null);
    try {
      /*
       * Genau die vier Angaben, die es seit V1 sind. Kategorie folgt der
       * Familie, Ausgangsprodukt und Parameter sind Anschauung -- sie stehen
       * bewusst NICHT in diesem Aufruf.
       */
      const created = await api.createArticle({
        articleNumber: nummer.trim(),
        familyId: Number(familyId),
        shortDescription: bezeichnung.trim() === "" ? null : bezeichnung.trim(),
        roleCodes: rollen,
        ...(wiederverwenden ? { allowReusedNumber: true } : {}),
      });
      /*
       * DIE VARIANTE ENTSTEHT ALS BEZIEHUNG, NICHT ALS KOPIE.
       *
       * Zuordnung zum Modell, dann die Auspraegungen auf dessen Achsen --
       * mehr braucht es nicht, damit die neue SKU nachvollziehbar eine
       * Ausfuehrung desselben Produkts ist. Kein Merkmalswert wechselt
       * dabei den Besitzer, und keine Herkunft wird behauptet.
       *
       * Scheitert einer der beiden Schritte, bleibt der Artikel bestehen
       * und der Fehler steht da. Ihn wieder zu loeschen waere schlimmer:
       * HOMEBASE hat keinen Loeschweg, und ein halb angelegtes Produkt
       * ohne Modellbezug ist reparierbar -- ein verschwundenes nicht.
       */
      if (modell !== null && zielAusfuehrung !== null) {
        /*
         * DIE NEUE SKU IST EINE AUSFUEHRUNG -- UND TRAEGT DEREN WERTE.
         *
         * Bis zum 20.09.2026 wurde hier nur verknuepft. Das Ergebnis war
         * produktiv zu sehen: TP154-4KD2D-CRI entstand aus der Ausfuehrung
         * „4000 K / 90 Ra / 120°" und hatte keinen einzigen eigenen
         * Merkmalswert. Jetzt laeuft die Zuordnung ueber denselben Weg wie
         * jede andere (`assign-article`) -- samt Uebernahme der
         * Ausfuehrungswerte, die ein neuer Artikel naturgemaess noch nicht
         * traegt.
         */
        await api.assignArticleModel(created.id, modell.id);
        await api.assignArticleToExecution(zielAusfuehrung, { articleId: created.id, adoptMissing: true });
      } else if (modell !== null && modellAchsen.length > 0) {
        await api.assignArticleModel(created.id, modell.id);
        await api.createVariant(modell.id, {
          articleId: created.id,
          values: modellAchsen
            .filter((d) => modellWahl[d.id] !== undefined)
            .map((d) => ({ dimensionId: d.id, valueId: modellWahl[d.id]! })),
        });
      }
      onCreated(created.id);
    } catch (e) {
      if (e instanceof ApiError && e.code === "article_number_previously_deleted") {
        setNummerFrei(nummer.trim()); setWiederverwenden(false);
      }
      setError(e);
    } finally { setBusy(false); }
  }

  /*
   * DER FACHLICHE DOPPELGAENGER -- geprueft an der Konfiguration, nicht an
   * der Nummer.
   *
   * Zwei Artikel derselben Familie mit derselben gewaehlten Ausfuehrung
   * sind vermutlich dasselbe Produkt, auch wenn die Nummern verschieden
   * sind. Das ist eine WARNUNG: es kann triftige Gruende geben, und
   * HOMEBASE entscheidet das nicht. Gewarnt wird VOR dem Anlegen.
   */
  const doppelgaenger = useMemo(() => {
    if (ausgang === null) return [];
    const gewaehlt = achsen
      .map((d) => d.options.find((o) => o.id === parameter[d.id]))
      .filter((o): o is VariantOptionValue => o !== undefined);
    if (gewaehlt.length === 0) return [];
    /* Der Doppelgaenger waere ein Artikel, dessen Nummer jede gewaehlte
       Ausfuehrung traegt -- mehr laesst sich ohne Modellebene nicht
       belegen, und mehr wird deshalb auch nicht behauptet. */
    const teile = gewaehlt.map((o) => String(o.raw ?? o.num ?? o.code ?? o.text ?? ""))
      .filter((x) => x !== "");
    if (teile.length === 0) return [];
    return artikel.filter((a) => a.id !== ausgang.id
      && teile.every((tl) => a.articleNumber.includes(tl)));
  }, [artikel, ausgang, achsen, parameter]);

  /*
   * DIESELBE AUSFUEHRUNG GIBT ES SCHON -- und das muss VOR dem Anlegen
   * dastehen.
   *
   * Seit der ersten realen Modellsetzung ist dieser Fall erreichbar: wer
   * ein Vorbild waehlt und keine Achse aendert, beschreibt genau die
   * Variante, die es bereits gibt. Der Server weist das zu Recht ab
   * (`variant_exists`) -- aber ERST nach dem Anlegen des Artikels. Dann
   * steht ein Produkt in der Datenbank, das keinem Modell zugeordnet ist,
   * und der Assistent bleibt mit einer Fehlermeldung stehen.
   *
   * HOMEBASE hat keinen Loeschweg. Ein solcher Halbzustand ist deshalb
   * teurer als jede Warnung -- die Pruefung gehoert davor.
   */
  const varianteVergeben = useMemo(() => {
    if (modell === null || modellAchsen.length === 0) return null;
    const meine = modellAchsen
      .filter((d) => modellWahl[d.id] !== undefined)
      .map((d) => `${d.id}:${modellWahl[d.id]}`).sort().join(",");
    if (meine === "") return null;
    return modell.variants.find((v) => {
      /* Die Ausfuehrung, aus der der Assistent kommt, ist nicht „vergeben" --
         sie ist das Ziel, solange sie noch keinen Artikel hat. */
      if (ausfuehrung && v.id === ausfuehrung.variantId && v.article === null) return false;
      const seine = v.values.map((w) => `${w.dimensionId}:${w.valueId}`).sort().join(",");
      return seine === meine;
    }) ?? null;
  }, [modell, modellAchsen, modellWahl, ausfuehrung]);

  /* Traegt die Auswahl noch genau die Werte der Ausgangs-Ausfuehrung? */
  const zielAusfuehrung = useMemo(() => {
    if (!ausfuehrung) return null;
    const soll = ausfuehrung.values.map((w) => `${w.dimensionId}:${w.valueId}`).sort().join(",");
    const ist = Object.entries(modellWahl).map(([d, v]) => `${d}:${v}`).sort().join(",");
    return soll === ist ? ausfuehrung.variantId : null;
  }, [ausfuehrung, modellWahl]);

  /* Eine Nummer, die es schon gibt, weist der Server ab (409). Hier steht
     sie frueher -- im Assistenten, nicht erst nach dem Knopfdruck. */
  const nummerVergeben = useMemo(() => {
    const n = nummer.trim().toLowerCase();
    return n !== "" && artikel.some((a) => a.articleNumber.toLowerCase() === n);
  }, [artikel, nummer]);

  /*
   * UND EINE NUMMER, DIE IM PAPIERKORB LIEGT.
   *
   * `artikel` ist die Familienliste -- und die enthaelt weggeraeumte
   * Produkte nicht mehr. Die fruehe Warnung oben war damit blind fuer
   * genau den Fall, den der Server sperrt: der Benutzer tippte eine
   * Nummer, sah keinen Hinweis, fuellte den Assistenten aus, drueckte
   * „Anlegen" -- und bekam erst dort eine Absage, die ihn in den
   * Papierkorb schickt.
   *
   * Gefragt wird deshalb DIESELBE Liste, die der Papierkorb zeigt. Faende
   * der Assistent hier etwas, was dort nicht steht, waere das ein Fehler
   * -- und keiner, den der Benutzer aufloesen koennte.
   */
  const [papierkorb, setPapierkorb] = useState<readonly ArticleTrashEntry[]>([]);
  useEffect(() => {
    let weg = false;
    /* Scheitert der Aufruf, bleibt die Liste leer: der Server sperrt
       trotzdem, und die Anlage soll an einer fehlenden Warnung nicht
       scheitern. */
    api.trash().then((r) => { if (!weg) setPapierkorb(r.items); }).catch(() => {});
    return () => { weg = true; };
  }, []);
  const nummerImPapierkorb = useMemo(() => {
    const n = nummer.trim().toLowerCase();
    if (n === "") return null;
    return papierkorb.find((e) => e.articleNumber.toLowerCase() === n) ?? null;
  }, [papierkorb, nummer]);

  /*
   * Ausfuehrung geaendert, Bezeichnung woertlich uebernommen -- das ist
   * selten Absicht und immer erwaehnenswert.
   *
   * Der Hinweis zaehlt BEIDE Wege: die blosse Anschauung des
   * Variantenraums und die echte Aenderung auf einer Modellachse. Nur den
   * ersten zu zaehlen hiesse, ihn genau dort zu verschweigen, wo er am
   * meisten zaehlt: `HB UGR | … | CRI:90` als Bezeichnung einer
   * CRI-80-Ausfuehrung waere eine falsche Produktangabe.
   */
  const achseGeaendert = modellAchsen.some((d) => {
    const gewaehlt = modellWahl[d.id];
    if (gewaehlt === undefined) return false;
    const vorher = basis?.variant?.values.find((w) => w.dimensionId === d.id)?.valueId ?? null;
    return vorher !== null && vorher !== gewaehlt;
  });
  const bezeichnungUnveraendert = ausgang !== null
    && bezeichnung.trim() !== ""
    && bezeichnung.trim() === (ausgang.shortDescription ?? "").trim()
    && (Object.keys(parameter).length > 0 || achseGeaendert);

  /*
   * Wann ein Schritt beantwortet ist.
   *
   * Vier und fuenf sind IMMER beantwortet: sie sind Anschauung. Wer nichts
   * waehlt, hat sich nicht geweigert -- es gibt oft nichts zu waehlen.
   */
  const fertig: Record<Schritt, boolean> = {
    1: categoryId !== "",
    2: familyId !== "",
    3: anlageart !== null,
    /* Schritt 5 ist beantwortet, sobald die Ausfuehrung nicht schon
       existiert -- weitergehen hiesse, auf einen Fehler zuzulaufen. */
    4: true,
    5: varianteVergeben === null,
    6: nummer.trim() !== "",
    7: nummer.trim() !== "" && familyId !== "" && varianteVergeben === null,
  };

  /*
   * OHNE VORBILD GIBT ES NICHTS ANZUSEHEN.
   *
   * Fall B und C ueberspringen Ausgangsprodukt und Parameter. Sie
   * anzuzeigen und leer zu lassen waere die Sorte Schritt, die man
   * wegklickt, ohne sie zu lesen.
   */
  /*
   * WAS OHNE VORBILD UEBERSPRUNGEN WIRD -- UND WAS NICHT MEHR.
   *
   * Bis zum 12.09.2026 fielen ohne Vorbild BEIDE Schritte weg: das
   * Ausgangsprodukt (4) und die Variantenparameter (5). Fuer das
   * Ausgangsprodukt ist das richtig -- es gibt keines. Fuer die
   * Variantenparameter war es eine Luecke: wer das erste Produkt einer
   * technischen Plattform anlegt, hat naturgemaess kein Vorbild und kam
   * damit nie an die Modellachsen. Genau dieser Weg --
   * Familie -> Modell -> Ausfuehrung -> SKU -- war dadurch nicht gehbar.
   *
   * Schritt 5 entfaellt jetzt nur noch, wenn es fuer diese Familie kein
   * Produktmodell gibt UND dieser Nutzer auch keines anlegen darf. Dann
   * waere der Schritt eine leere Seite; sonst ist er der Ort, an dem die
   * Plattform gewaehlt oder angelegt wird.
   */
  const uebersprungen = (x: Schritt): boolean =>
    anlageart !== "variante"
    && (x === 4 || (x === 5 && modelleAlle.length === 0 && !mayWriteModels));

  const weiter = (): void => {
    setSchritt((s) => {
      let n = s;
      do { n = (n + 1) as Schritt; } while (n < 7 && uebersprungen(n));
      return n > 7 ? 7 : n;
    });
  };
  const zurueck = (): void => {
    setSchritt((s) => {
      let n = s;
      do { n = (n - 1) as Schritt; } while (n > 1 && uebersprungen(n));
      return n < 1 ? 1 : n;
    });
  };

  return (
    <div className="neu__backdrop" role="dialog" aria-modal="true" aria-label={t("neu.title")}
         onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}>
      <div className="neu neu--assistent">
        <h2 className="neu__title">{t("neu.title")}</h2>
        {ausfuehrung && (
          <p className="neu__hint neu__ausfuehrung" data-testid="neu-ausfuehrung">
            {t("neu.fromExecution", { execution: ausfuehrung.label })}
          </p>
        )}

        <Schrittleiste aktuell={schritt} fertig={fertig} aufSchritt={setSchritt} i18n={i18n} />

        {schritt === 1 && (
          <fieldset className="neu__field">
            <legend>{t("neu.stepCategory")}</legend>
            <small className="neu__hint">{t("neu.stepCategoryHint")}</small>
            <div className="neu__wahl">
              {kategorien.map((c) => (
                <button key={c.id} type="button"
                  className={`neu__kachel${String(c.id) === categoryId ? " neu__kachel--aktiv" : ""}`}
                  onClick={() => {
                    setCategoryId(String(c.id));
                    /* Passt die bisherige Familie nicht mehr, faellt sie weg --
                       still stehen zu lassen waere ein Widerspruch. */
                    if (gewaehlteFamilie && gewaehlteFamilie.categoryId !== c.id) setFamilyId("");
                    setSchritt(2);
                  }}>
                  {categoryLabel(i18n, c.code, c.name)}
                </button>
              ))}
            </div>
          </fieldset>
        )}

        {schritt === 2 && (
          <>
            <label className="neu__field">
              <span>{t("neu.stepFamily")}</span>
              <select className="input" value={familyId} autoFocus
                onChange={(e) => setFamilyId(e.target.value)}>
                <option value="">{t("neu.familyChoose")}</option>
                {familien.map((f) => (<option key={f.id} value={f.id}>{f.name}</option>))}
              </select>
              <small className="neu__hint">
                {kategorie
                  ? t("neu.categoryFollows", {
                      category: categoryLabel(i18n, kategorie.code, kategorie.name),
                    })
                  : t("neu.familyHint")}
              </small>
            </label>
            {/*
              * KEINE SACKGASSE BEI EINER NEUEN BAUREIHE.
              *
              * Bis zum 11.09.2026 endete der Assistent hier, wenn es die
              * Familie noch nicht gab: es gab keinen Weg, sie anzulegen --
              * weder hier noch anderswo. Jetzt entsteht sie an dieser
              * Stelle, und der Anlageprozess laeuft weiter.
              */}
            <NeueFamilie categoryId={categoryId} i18n={i18n}
              onAngelegt={(f) => {
                setNeueFamilie(f);
                setFamilyId(String(f.id));
                /* Eine neue Familie hat kein Vorbild -- Fall C. */
                setAnlageart("ohne");
              }} />
          </>
        )}

        {schritt === 3 && (
          <fieldset className="neu__field">
            <legend>{t("neu.stepKind")}</legend>
            <small className="neu__hint">{t("neu.stepKindHint")}</small>
            <div className="neu__wahl">
              <button type="button"
                className={`neu__kachel${anlageart === "variante" ? " neu__kachel--aktiv" : ""}`}
                disabled={artikel.length === 0}
                onClick={() => setAnlageart("variante")}>
                <strong>{t("neu.kindVariant")}</strong>
                <span>{artikel.length === 0
                  ? t("neu.kindVariantNone")
                  : t("neu.kindVariantHint", { n: artikel.length })}</span>
              </button>
              <button type="button"
                className={`neu__kachel${anlageart === "ohne" ? " neu__kachel--aktiv" : ""}`}
                onClick={() => { setAnlageart("ohne"); setAusgangsId(null); }}>
                <strong>{t("neu.kindPlain")}</strong>
                <span>{t("neu.kindPlainHint")}</span>
              </button>
            </div>
            <p className="neu__hint">{i18n.tCode("neuCase", fall)}</p>
          </fieldset>
        )}

        {schritt === 4 && (
          <fieldset className="neu__field">
            <legend>{t("neu.stepBase")}</legend>
            <small className="neu__hint">{t("neu.stepBaseHint")}</small>
            {ladeArtikel
              ? <p className="neu__leer">{t("common.loading")}</p>
              : artikel.length === 0
                /* Kein Bestand in dieser Familie ist ein normaler Fall -- die
                   erste Leuchte einer Familie hat kein Vorbild. */
                ? <p className="neu__leer">{t("neu.baseNone")}</p>
                : (
                  <div className="neu__wahl neu__wahl--liste">
                    {artikel.map((a) => (
                      <button key={a.id} type="button"
                        className={`neu__kachel${a.id === ausgangsId ? " neu__kachel--aktiv" : ""}`}
                        onClick={() => setAusgangsId(a.id === ausgangsId ? null : a.id)}>
                        <strong>{a.articleNumber}</strong>
                        {a.shortDescription && <span>{a.shortDescription}</span>}
                      </button>
                    ))}
                  </div>
                )}
          </fieldset>
        )}

        {schritt === 5 && (
          <fieldset className="neu__field">
            <legend>{t("neu.stepVariant")}</legend>
            {/*
              * ZWEI WEGE, WEIL ES ZWEI WAHRHEITEN SIND.
              *
              * Traegt das Vorbild ein Produktmodell, sind die Achsen die des
              * MODELLS und die Wahl ist eine echte Festlegung: sie wird zur
              * Auspraegung der neuen SKU. Ohne Modell bleibt es beim Zeigen
              * des Variantenraums -- und der Assistent sagt das auch.
              */}
            {/*
              * DIE PLATTFORM STEHT VOR DER AUSFUEHRUNG.
              *
              * Fuehrt die Familie mehr als ein Produktmodell, waehlt ein
              * Mensch. Bei genau einem ist die Wahl getroffen, und die
              * Auswahl waere eine Frage ohne Alternative -- sie wird dann
              * nur genannt. Fuehrt die Familie keines, sagt der Assistent
              * das und faellt auf den Variantenraum zurueck; er behauptet
              * keine Modellebene, die es nicht gibt.
              */}
            {modelleAlle.length > 1 && (
              <label className="neu__field">
                <span>{t("neu.model")}</span>
                <select className="list__select" value={modellWunsch ?? ""}
                  onChange={(e) => setModellWunsch(e.target.value ? Number(e.target.value) : null)}>
                  <option value="">{t("neu.modelChoose")}</option>
                  {modelleAlle.map((m) => (
                    <option key={m.id} value={m.id}>{m.code} — {m.name}</option>
                  ))}
                </select>
                <small className="neu__hint">{t("neu.modelHint")}</small>
              </label>
            )}
            {modelleAlle.length === 1 && modell !== null && (
              <small className="neu__hint">{t("neu.modelOnly", { model: modell.name })}</small>
            )}
            {/*
              * FEHLT DIE PLATTFORM, ENDET DER VORGANG NICHT.
              *
              * Bis hierher stand ein Hinweis: „Unter Produktmodelle laesst
              * sich eines anlegen." Das ist richtig und trotzdem eine
              * Sackgasse -- der Benutzer muesste den Bereich wechseln, das
              * Modell anlegen, zurueckkommen und von vorn beginnen.
              *
              * Die Familie ist an dieser Stelle bereits bekannt, und ein
              * Modell braucht nicht mehr als Kennung und Namen. Es entsteht
              * ueber DENSELBEN Weg wie im Modellbereich (`api.createModel`)
              * und traegt damit dieselbe Pruefung, dieselben Rechte und
              * dasselbe Protokoll.
              */}
            {!ladeModelle && modelleAlle.length === 0 && (
              mayWriteModels && gewaehlteFamilie !== null
                ? <NeuesModell familyId={gewaehlteFamilie.id} familyName={gewaehlteFamilie.name}
                    i18n={i18n}
                    onAngelegt={(m) => {
                      setNeueModelle((p) => [...p, {
                        id: m.id, code: m.code, name: m.name,
                      } as ProductModelListItem]);
                      setModellWunsch(m.id);
                      setzeModell(m);
                    }}
                    onFehler={setError} />
                : <p className="neu__leer">{t("neu.modelNone")}</p>
            )}
            {/*
              * EIN FRISCHES MODELL HAT NOCH KEINE ACHSEN -- und auch dafuer
              * muss niemand den Vorgang verlassen. Gepflegt wird mit DERSELBEN
              * Komponente wie im Modellbereich; es gibt keine zweite
              * Achsenpflege.
              */}
            {modell !== null && modellAchsen.length === 0 && mayWriteModels && (
              <div className="neu__modellpflege">
                <ModelDimensions model={modell} definitions={meta.propertyDefinitions}
                  mayWrite onChanged={setzeModell} onError={setError} />
              </div>
            )}

            {ladeModelle || ladeModell || ladeAchsen
              ? <p className="neu__leer">{t("common.loading")}</p>
              : modell !== null && modellAchsen.length === 0
                ? <p className="neu__leer">{t("neu.modelNoAxes", { model: modell.name })}</p>
                /*
                 * FALL A OHNE VORBILD BLEIBT EINE OFFENE FRAGE.
                 *
                 * Wer „Variante eines bestehenden Produkts" gewaehlt und noch
                 * kein Vorbild benannt hat, bekommt weiterhin die
                 * Aufforderung -- nicht die Achsen. Die Modellebene zu
                 * zeigen hiesse hier, den fehlenden Schritt zu ueberspringen.
                 * Fuer die Faelle OHNE Vorbild (neues Produkt in bestehender
                 * Familie, erstes Produkt einer neuen Familie) fuehrt dagegen
                 * die Familie zum Modell und das Modell zu den Achsen.
                 */
                : anlageart === "variante" && ausgang === null
                  ? <p className="neu__leer">{t("neu.variantNoBase")}</p>
                  : ausgang === null && modell === null
                  ? <p className="neu__leer">{t("neu.variantNoBase")}</p>
                  : modell !== null && modellAchsen.length > 0
                  ? (
                    <>
                      <small className="neu__hint">
                        {t("neu.modelAxesHint", { model: modell.name })}
                      </small>
                      {modellAchsen.map((d) => (
                        <ModellAchse key={d.id} d={d} i18n={i18n} cct={meta.cctValues}
                          gewaehlt={modellWahl[d.id] ?? null}
                          herkunft={basis?.variant?.values.find((w) => w.dimensionId === d.id)?.valueId ?? null}
                          waehle={(v) => setModellWahl((p) => ({ ...p, [d.id]: v }))} />
                      ))}
                      {varianteVergeben !== null && (
                        <p className="neu__warnung" role="alert">
                          {t("neu.variantTaken", {
                            number: varianteVergeben.article?.articleNumber
                              ?? t("neu.variantTakenVirtual"),
                          })}
                        </p>
                      )}
                    </>
                  )
                  : achsen.length === 0
                    ? <p className="neu__leer">{t("neu.variantNone")}</p>
                    : (
                      <>
                        <small className="neu__hint">{t("neu.stepVariantHint")}</small>
                        {achsen.map((d) => (
                          <Achse key={d.id} d={d} gewaehlt={parameter[d.id] ?? null} i18n={i18n}
                            waehle={(v) => setParameter((p) => {
                              const n = { ...p };
                              if (n[d.id] === v) delete n[d.id]; else n[d.id] = v;
                              return n;
                            })} />
                        ))}
                      </>
                    )}
          </fieldset>
        )}

        {schritt === 6 && (
          <>
            <label className="neu__field">
              <span>{t("neu.articleNumber")}</span>
              <input className="input" value={nummer} maxLength={100} autoFocus
                onChange={(e) => setNummer(e.target.value)} />
              {/*
                * KEINE AUTOMATISCHE NUMMER -- und zwar begruendet.
                *
                * Die Nummernbildung ist je Familie verschieden, und
                * dieselbe Stelle bedeutet Verschiedenes: in
                * `HB200-U4KD2D-85-CRI` ist 85 der Abstrahlwinkel und
                * `-CRI` ein Wort, dessen Wert (90) gar nicht in der Nummer
                * steht; in `HB200-P5KD2D-90` ist 90 wieder der Winkel, und
                * der CRI (80) fehlt ebenfalls. Aus diesem Muster eine
                * Regel abzuleiten hiesse, sie fuer 36 Familien zu
                * behaupten. Der Assistent zeigt deshalb die vorhandenen
                * Nummern und ueberlaesst die Wahl dem Menschen.
                */}
              {ausgang !== null && (
                <small className="neu__hint">
                  {t("neu.numberBase", { number: ausgang.articleNumber })}
                </small>
              )}
              {nummerVergeben && (
                <small className="neu__warnung" role="alert">{t("neu.numberTaken")}</small>
              )}
              {nummerImPapierkorb !== null && (
                <small className="neu__warnung" role="alert">
                  {t("neu.numberInTrash", { number: nummerImPapierkorb.articleNumber })}
                  {onOpenTrash !== undefined && (
                    <> <button type="button" className="neu__link" onClick={onOpenTrash}>
                      {t("neu.numberInTrashOpen")}
                    </button></>
                  )}
                </small>
              )}
              {/* Die Nummer ist die Identitaet. Das steht hier, BEVOR sie
                  vergeben ist -- danach waere der Hinweis eine Entschuldigung. */}
              <small className="neu__hint">{t("neu.articleNumberHint")}</small>
            </label>

            <label className="neu__field">
              <span>{t("neu.shortDescription")}</span>
              {/*
                * Ein mehrzeiliges Feld, kein einzeiliges.
                *
                * Quellbezeichnungen sind regelmaessig ueber hundert Zeichen
                * lang. In einer Zeile sieht man davon ein Drittel und schiebt
                * den Rest hin und her.
                */}
              <textarea className="input neu__textarea" value={bezeichnung} maxLength={500} rows={3}
                onChange={(e) => { setBezeichnungBeruehrt(true); setBezeichnung(e.target.value); }} />
              <small className="neu__hint">{t("neu.shortDescriptionHint")}</small>
              {/* Der Vorschlag ist als solcher gekennzeichnet, solange er
                  unveraendert dasteht -- ein uebernommener Satz ist keine
                  gepruefte Aussage ueber dieses Produkt. */}
              {!bezeichnungBeruehrt && ausgang !== null && bezeichnung !== "" && (
                <small className="neu__hint">
                  {t("neu.shortDescriptionFromBase", { number: ausgang.articleNumber })}
                </small>
              )}
              {bezeichnungUnveraendert && (
                <small className="neu__warnung" role="alert">
                  {t("neu.shortDescriptionUnchanged")}
                </small>
              )}
              {bezeichnung.trim() === "" && (
                <small className="neu__warnung" role="alert">{t("neu.shortDescriptionMissing")}</small>
              )}
            </label>

            <fieldset className="neu__field neu__roles">
              <legend>{t("neu.roles")}</legend>
              {meta.roles.map((r) => (
                <label key={r.code} className="neu__role">
                  <input type="checkbox" checked={rollen.includes(r.code)}
                    onChange={(e) => setRollen((v) => e.target.checked
                      ? [...v, r.code] : v.filter((c) => c !== r.code))} />
                  {i18n.tCode("articleRole", r.code, r.name)}
                </label>
              ))}
            </fieldset>
          </>
        )}

        {schritt === 7 && (
          <Zusammenfassung
            fall={fall} doppelgaenger={doppelgaenger}
            modell={modell} modellAchsen={modellAchsen} modellWahl={modellWahl}
            basisVariante={basis?.variant ?? null} cct={meta.cctValues}
            neueFamilie={neueFamilie !== null && String(neueFamilie.id) === familyId}
            kategorie={kategorie ? categoryLabel(i18n, kategorie.code, kategorie.name) : null}
            familie={gewaehlteFamilie?.name ?? null}
            ausgang={ausgang} achsen={achsen} parameter={parameter}
            nummer={nummer.trim()} bezeichnung={bezeichnung.trim()}
            rollen={rollen.map((c) => i18n.tCode("articleRole", c, c))}
            i18n={i18n} />
        )}

        {error !== null && <p className="neu__error" role="alert">{errorText(i18n, error)}</p>}
        {nummerFrei === nummer.trim() && nummerFrei !== null && (
          <label className="neu__field neu__wiederverwenden" data-testid="neu-wiederverwenden">
            <input type="checkbox" checked={wiederverwenden}
              onChange={(e) => setWiederverwenden(e.target.checked)} />
            <span>{t("neu.reuseNumber", { number: nummerFrei })}</span>
          </label>
        )}

        <div className="neu__actions">
          {schritt > 1 && (
            <button className="btn" disabled={busy} onClick={zurueck}>{t("neu.back")}</button>
          )}
          {schritt < 7
            ? <button className="btn btn--primary" disabled={!fertig[schritt]} onClick={weiter}>
                {t("neu.next")}
              </button>
            : <button className="btn btn--primary" disabled={!fertig[7] || busy} onClick={anlegen}>
                {t("neu.create")}
              </button>}
          <button className="btn" disabled={busy} onClick={onClose}>{t("common.cancel")}</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Die Schrittleiste.
 *
 * Sie zeigt, wo man ist und wie weit es noch geht -- und laesst zurueck zu
 * jedem bereits beantworteten Schritt. Vorwaerts springen kann man nicht: der
 * naechste Schritt haengt am vorigen.
 */
function Schrittleiste({ aktuell, fertig, aufSchritt, i18n }: {
  aktuell: Schritt;
  fertig: Record<Schritt, boolean>;
  aufSchritt: (s: Schritt) => void;
  i18n: I18n;
}): React.JSX.Element {
  const namen: Record<Schritt, string> = {
    1: i18n.t("neu.stepCategory"), 2: i18n.t("neu.stepFamily"),
    3: i18n.t("neu.stepKind"), 4: i18n.t("neu.stepBase"),
    5: i18n.t("neu.stepVariant"), 6: i18n.t("neu.stepIdentity"),
    7: i18n.t("neu.stepSummary"),
  };
  return (
    <ol className="neu__schritte">
      {SCHRITTE.map((s) => {
        const erreichbar = s <= aktuell || SCHRITTE.slice(0, s - 1).every((v) => fertig[v]);
        return (
          <li key={s} className={`neu__schritt${s === aktuell ? " neu__schritt--aktiv" : ""}`}>
            <button type="button" className="neu__schrittknopf" disabled={!erreichbar}
              aria-current={s === aktuell ? "step" : undefined}
              onClick={() => aufSchritt(s)}>
              <span className="neu__schrittnr">{s}</span>
              <span className="neu__schrittname">{namen[s]}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Eine MODELLACHSE mit ihren zulaessigen Werten.
 *
 * Der Unterschied zur `Achse` darunter ist kein optischer: dort waehlt man
 * zur Anschauung, hier legt man die Auspraegung der neuen SKU fest. Deshalb
 * ist die Auswahl hier KEIN Umschalter -- eine Achse ohne Wert waere eine
 * Variante, die auf dieser Achse nichts aussagt, und das ist selten
 * gemeint. Der Wert des Vorbilds ist gekennzeichnet, damit die Aenderung
 * sichtbar bleibt.
 */
function ModellAchse({ d, gewaehlt, herkunft, waehle, i18n, cct }: {
  d: ModelDimension;
  gewaehlt: number | null;
  /** Was das Ausgangsprodukt auf dieser Achse traegt. */
  herkunft: number | null;
  waehle: (valueId: number) => void;
  i18n: I18n;
  /** Der Farbtemperaturkatalog -- fuer die Einordnung neben dem Kelvinwert. */
  cct: readonly CctValue[];
}): React.JSX.Element {
  return (
    <div className="neu__achse">
      <span className="neu__achsenname">
        {i18n.tCode("prop", d.definition, d.definition)}
      </span>
      <div className="neu__wahl">
        {d.values.map((v) => (
          <button key={v.id} type="button"
            className={`neu__kachel neu__kachel--klein${v.id === gewaehlt ? " neu__kachel--aktiv" : ""}`}
            onClick={() => waehle(v.id)}>
            {dimensionText(d, v, i18n, cct)}
            {v.id === herkunft && (
              <span className="neu__standard">{i18n.t("neu.modelFromBase")}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Eine Variantenachse mit ihren Ausfuehrungen. Auswahl ist ein Umschalter. */
function Achse({ d, gewaehlt, waehle, i18n }: {
  d: VariantDimension;
  gewaehlt: number | null;
  waehle: (valueId: number) => void;
  i18n: I18n;
}): React.JSX.Element {
  return (
    <div className="neu__achse">
      <span className="neu__achsenname">
        {i18n.tCode("prop", d.definition.code, d.definition.code)}
      </span>
      <div className="neu__wahl">
        {d.options.map((o) => (
          <button key={o.id} type="button"
            className={`neu__kachel neu__kachel--klein${o.id === gewaehlt ? " neu__kachel--aktiv" : ""}`}
            onClick={() => waehle(o.id)}>
            {optionText(d.definition, o, i18n)}
            {o.isStandard && <span className="neu__standard">{i18n.t("varianten.standard")}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Der Wortlaut einer Ausfuehrung.
 *
 * `raw` zuerst: Werte wie `50° x 90°` haben keine eigene Struktur und wuerden
 * jede Formatierung verlieren.
 */
function optionText(def: PropertyDefinition, o: VariantOptionValue, i18n: I18n): string {
  return o.raw ?? displayValue(def, {
    id: o.id, definition: def.code, valueIndex: 0, num: o.num, operator: null,
    min: o.min, max: o.max, bool: o.bool, code: o.code, text: o.text,
    origin: "import", method: "uebernommen", quality: "pruefung_erforderlich",
    sourceSegment: null, updatedAt: "",
  }, i18n, false);
}

/**
 * Eine neue Produktfamilie -- im Assistenten, nicht daneben.
 *
 * DREI ANGABEN: Kategorie (steht schon fest), Kennung und Name. Mehr
 * verlangt `product_families` nicht, und mehr zu verlangen hiesse, die
 * Anlage genau dann zu verhindern, wenn sie gebraucht wird -- beim ersten
 * Produkt einer Baureihe, ueber die noch wenig bekannt ist.
 *
 * DIE AEHNLICHE FAMILIE WIRD GEZEIGT, BEVOR ANGELEGT WIRD. Nicht
 * zusammengefuehrt, nicht umbenannt: gleich zu heissen ist erlaubt, und
 * ob es dieselbe Baureihe ist, weiss nur ein Mensch.
 */
/**
 * Ein Produktmodell aus dem Produktvorgang heraus anlegen.
 *
 * Zwei Angaben, mehr nicht: Kennung und Name. Die Produktfamilie steht an
 * dieser Stelle bereits fest -- sie noch einmal zu fragen waere eine Frage
 * ohne Alternative, und ein Modell gehoert zu genau einer Familie.
 *
 * Es entsteht ueber `api.createModel` -- denselben Weg, den der
 * Modellbereich benutzt. Keine zweite Modellanlage, keine zweite Pruefung,
 * kein zweites Protokoll.
 */
function NeuesModell({ familyId, familyName, i18n, onAngelegt, onFehler }: {
  familyId: number;
  familyName: string;
  i18n: I18n;
  onAngelegt: (m: ProductModelDetail) => void;
  onFehler: (e: unknown) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [offen, setOffen] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  async function anlegen(): Promise<void> {
    setBusy(true);
    try { onAngelegt(await api.createModel({ code: code.trim(), name: name.trim(), familyId })); }
    catch (e) { onFehler(e); } finally { setBusy(false); }
  }

  if (!offen) {
    return (
      <div className="neu__modellneu">
        <p className="neu__leer">{t("neu.modelNoneHere", { family: familyName })}</p>
        <button type="button" className="btn btn--small"
          onClick={() => setOffen(true)}>{t("neu.modelAdd")}</button>
      </div>
    );
  }
  return (
    <fieldset className="neu__field neu__modellform">
      <legend>{t("neu.modelAddTitle")}</legend>
      <small className="neu__hint">{t("neu.modelForFamily", { family: familyName })}</small>
      <label className="neu__field">
        <span>{t("neu.modelCode")}</span>
        <input className="input" value={code} maxLength={60} autoFocus
          onChange={(e) => setCode(e.target.value)} />
        <small className="neu__hint">{t("neu.modelCodeHint")}</small>
      </label>
      <label className="neu__field">
        <span>{t("neu.modelName")}</span>
        <input className="input" value={name} maxLength={120}
          onChange={(e) => setName(e.target.value)} />
      </label>
      <div className="neu__modellactions">
        <button type="button" className="btn" onClick={() => setOffen(false)}>
          {t("common.cancel")}
        </button>
        <button type="button" className="btn btn--primary"
          disabled={busy || code.trim() === "" || name.trim() === ""}
          onClick={() => void anlegen()}>{t("neu.modelCreate")}</button>
      </div>
    </fieldset>
  );
}

/*
 * DIESELBE FAMILIENANLAGE AN ZWEI STELLEN -- UND NUR EINE LOGIK.
 *
 * Sie entstand fuer den Produktassistenten. Seit dem 12.09.2026 braucht sie
 * auch die Modellanlage: ein Produktmodell gehoert zu genau einer Familie,
 * und wer dort feststellt, dass sie fehlt, soll den Vorgang nicht abbrechen
 * muessen. Exportiert statt kopiert -- eine zweite Familienanlage waere eine
 * zweite Wahrheit ueber Kennung, Dublettenwarnung und Anlageweg.
 */
/**
 * Eine neue HAUPTKATEGORIE, ohne den laufenden Vorgang zu verlassen
 * (UI/UX 001).
 *
 * Dieselbe Gestalt wie `NeueFamilie` darunter -- und aus demselben Grund:
 * wer eine Produktwelt anlegt, die es in HOMEBASE noch nicht gibt, soll
 * nicht in einen anderen Bereich wechseln und den halb ausgefuellten Vorgang
 * verlieren. Der Unterschied liegt im Recht: eine Kategorie gliedert den
 * ganzen Katalog, deshalb `system.manage`. Wer es nicht hat, sieht diesen
 * Weg nicht -- und waehlt aus dem Vorhandenen.
 */
export function NeueHauptkategorie({ i18n, onAngelegt }: {
  i18n: I18n;
  onAngelegt: (c: CategoryItem) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [offen, setOffen] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [aehnlich, setAehnlich] = useState<readonly CategoryItem[]>([]);
  const [fehler, setFehler] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  /* Die Warnung entsteht beim Tippen, nicht beim Speichern. */
  useEffect(() => {
    if (!offen || (code.trim() === "" && name.trim() === "")) { setAehnlich([]); return; }
    let aktuell = true;
    const zeit = setTimeout(() => {
      void api.similarCategories(code.trim(), name.trim())
        .then((r) => { if (aktuell) setAehnlich(r.items); })
        .catch(() => { if (aktuell) setAehnlich([]); });
    }, 250);
    return () => { aktuell = false; clearTimeout(zeit); };
  }, [offen, code, name]);

  async function anlegen(): Promise<void> {
    setBusy(true); setFehler(null);
    try {
      onAngelegt(await api.createCategory({ code: code.trim(), name: name.trim() }));
      setOffen(false); setCode(""); setName("");
    } catch (e) { setFehler(e); } finally { setBusy(false); }
  }

  if (!offen) {
    return (
      <div className="neu__familieneu">
        <button type="button" className="btn btn--small" data-testid="ux-kategorie-neu"
          onClick={() => setOffen(true)}>{t("neu.categoryAdd")}</button>
      </div>
    );
  }
  return (
    <fieldset className="neu__field neu__familieform" data-testid="ux-kategorie-form">
      <legend>{t("neu.categoryAddTitle")}</legend>
      <label className="feld">
        <span className="feld__label">{t("neu.categoryCode")}</span>
        <input className="feld__input" value={code} maxLength={60} autoFocus
          onChange={(e) => setCode(e.target.value)} />
        <small className="feld__note">{t("neu.categoryCodeHint")}</small>
      </label>
      <label className="feld">
        <span className="feld__label">{t("neu.categoryName")}</span>
        <input className="feld__input" value={name} maxLength={120}
          onChange={(e) => setName(e.target.value)} />
      </label>
      {aehnlich.length > 0 && (
        <div className="neu__warnung" role="status" data-testid="ux-kategorie-dublette">
          <strong>{t("neu.categorySimilar")}</strong>
          <ul className="neu__aehnlich">
            {aehnlich.map((c) => (
              <li key={c.id}>{c.code} · {c.name} ({t("neu.familyArticles", { n: c.articleCount })})</li>
            ))}
          </ul>
          <span>{t("neu.categorySimilarHint")}</span>
        </div>
      )}
      {fehler !== null && <p className="neu__error" role="alert">{errorText(i18n, fehler)}</p>}
      <div className="neu__actions">
        <button className="btn btn--primary btn--small" disabled={busy || code.trim() === "" || name.trim() === ""}
          onClick={() => void anlegen()}>{t("neu.categoryCreate")}</button>
        <button className="btn btn--small" disabled={busy}
          onClick={() => { setOffen(false); setFehler(null); }}>{t("common.cancel")}</button>
      </div>
    </fieldset>
  );
}

export function NeueFamilie({ categoryId, i18n, onAngelegt }: {
  categoryId: string;
  i18n: I18n;
  onAngelegt: (f: FamilyItem) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [offen, setOffen] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [aehnlich, setAehnlich] = useState<readonly FamilyItem[]>([]);
  const [fehler, setFehler] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  /* Die Warnung entsteht beim Tippen, nicht beim Speichern -- danach waere
     sie eine Fehlermeldung statt einer Hilfe. */
  useEffect(() => {
    if (!offen || (code.trim() === "" && name.trim() === "")) { setAehnlich([]); return; }
    let aktuell = true;
    const zeit = setTimeout(() => {
      void api.similarFamilies(Number(categoryId || 0), code.trim(), name.trim())
        .then((r) => { if (aktuell) setAehnlich(r.items); })
        .catch(() => { if (aktuell) setAehnlich([]); });
    }, 250);
    return () => { aktuell = false; clearTimeout(zeit); };
  }, [offen, categoryId, code, name]);

  async function anlegen(): Promise<void> {
    setBusy(true); setFehler(null);
    try {
      onAngelegt(await api.createFamily({
        categoryId: Number(categoryId), code: code.trim(), name: name.trim(),
      }));
      setOffen(false); setCode(""); setName("");
    } catch (e) { setFehler(e); } finally { setBusy(false); }
  }

  if (!offen) {
    return (
      <div className="neu__familieneu">
        <button type="button" className="btn btn--small" disabled={categoryId === ""}
          onClick={() => setOffen(true)}>{t("neu.familyAdd")}</button>
      </div>
    );
  }
  return (
    <fieldset className="neu__field neu__familieform">
      <legend>{t("neu.familyAddTitle")}</legend>
      <label className="neu__field">
        <span>{t("neu.familyCode")}</span>
        <input className="input" value={code} maxLength={60} autoFocus
          onChange={(e) => setCode(e.target.value)} />
        <small className="neu__hint">{t("neu.familyCodeHint")}</small>
      </label>
      <label className="neu__field">
        <span>{t("neu.familyName")}</span>
        <input className="input" value={name} maxLength={120}
          onChange={(e) => setName(e.target.value)} />
      </label>
      {aehnlich.length > 0 && (
        <div className="neu__warnung" role="status">
          <strong>{t("neu.familySimilar")}</strong>
          <ul className="neu__aehnlich">
            {aehnlich.map((f) => (
              <li key={f.id}>
                {f.code} · {f.name} ({t("neu.familyArticles", { n: f.articleCount })})
              </li>
            ))}
          </ul>
          <span>{t("neu.familySimilarHint")}</span>
        </div>
      )}
      {fehler !== null && <p className="neu__error" role="alert">{errorText(i18n, fehler)}</p>}
      <div className="neu__actions">
        <button className="btn btn--primary btn--small" disabled={busy || code.trim() === "" || name.trim() === ""}
          onClick={() => void anlegen()}>{t("neu.familyCreate")}</button>
        <button className="btn btn--small" disabled={busy}
          onClick={() => { setOffen(false); setFehler(null); }}>{t("common.cancel")}</button>
      </div>
    </fieldset>
  );
}

/**
 * Was gleich entsteht -- und was nicht.
 *
 * Der zweite Teil ist der wichtigere. Wer vier Schritte lang ein
 * Ausgangsprodukt und seine Ausfuehrung ausgewaehlt hat, nimmt an, dass das
 * irgendwo landet. Es landet nicht, und das muss dastehen, bevor der Knopf
 * gedrueckt wird -- nicht hinterher als Ueberraschung in der Akte.
 */
function Zusammenfassung({
  fall, neueFamilie, doppelgaenger, modell, modellAchsen, modellWahl, basisVariante, cct,
  kategorie, familie, ausgang, achsen, parameter, nummer, bezeichnung, rollen, i18n,
}: {
  fall: "A" | "B" | "C";
  neueFamilie: boolean;
  doppelgaenger: readonly ArticleListItem[];
  modell: ProductModelDetail | null;
  modellAchsen: readonly ModelDimension[];
  modellWahl: Record<number, number>;
  basisVariante: ArticleDetail["variant"];
  cct: readonly CctValue[];
  kategorie: string | null;
  familie: string | null;
  ausgang: ArticleListItem | null;
  achsen: readonly VariantDimension[];
  parameter: Record<number, number>;
  nummer: string;
  bezeichnung: string;
  rollen: readonly string[];
  i18n: I18n;
}): React.JSX.Element {
  const t = i18n.t;
  const gewaehlteParameter = achsen
    .map((d) => {
      const o = d.options.find((x) => x.id === parameter[d.id]);
      return o
        ? `${i18n.tCode("prop", d.definition.code, d.definition.code)}: ${optionText(d.definition, o, i18n)}`
        : null;
    })
    .filter((x): x is string => x !== null);

  return (
    <div className="neu__summe">
      <p className="neu__fall">{i18n.tCode("neuCase", fall)}</p>
      <dl className="neu__summeliste">
        <dt>{t("neu.stepCategory")}</dt><dd>{kategorie ?? "—"}</dd>
        <dt>{t("neu.stepFamily")}</dt><dd>{familie ?? "—"}</dd>
        <dt>{t("neu.articleNumber")}</dt><dd><strong>{nummer === "" ? "—" : nummer}</strong></dd>
        <dt>{t("neu.shortDescription")}</dt><dd>{bezeichnung === "" ? "—" : bezeichnung}</dd>
        <dt>{t("neu.roles")}</dt><dd>{rollen.length === 0 ? "—" : rollen.join(" · ")}</dd>
      </dl>

      {/*
        * DER FACHLICHE DOPPELGAENGER -- als Warnung, nicht als Sperre.
        * HOMEBASE fuehrt nichts zusammen und benennt nichts um.
        */}
      {doppelgaenger.length > 0 && (
        <div className="neu__warnung" role="alert">
          <strong>{t("neu.twinTitle")}</strong>
          <ul className="neu__aehnlich">
            {doppelgaenger.map((a) => (
              <li key={a.id}>{a.articleNumber}{a.shortDescription ? ` · ${a.shortDescription}` : ""}</li>
            ))}
          </ul>
          <span>{t("neu.twinHint")}</span>
        </div>
      )}

      {/*
        * MODELLWISSEN, VARIANTENAENDERUNG UND SKU-EIGENES GETRENNT.
        *
        * Das ist die Aussage, um die es in diesem Block geht: was diese SKU
        * mit ihren Geschwistern teilt, steht am MODELL und wird nicht
        * kopiert. Was sie unterscheidet, ist die Auspraegung auf einer
        * Achse. Und was nur ihr gehoert, ist Nummer und Bezeichnung.
        */}
      {modell !== null && modellAchsen.length > 0 && (
        <div className="neu__modell">
          <h3 className="neu__nichttitel">{t("neu.modelTitle")}</h3>
          <p className="neu__hint">{t("neu.modelKnowledge", { model: modell.name })}</p>
          <dl className="neu__summeliste">
            <dt>{t("neu.modelChanges")}</dt>
            <dd>
              {(() => {
                const ist = (id: number): number | null =>
                  basisVariante?.values.find((w) => w.dimensionId === id)?.valueId ?? null;
                const geaendert = modellAchsen
                  .filter((d) => modellWahl[d.id] !== undefined && modellWahl[d.id] !== ist(d.id))
                  .map((d) => {
                    const alt = d.values.find((v) => v.id === ist(d.id));
                    const neu = d.values.find((v) => v.id === modellWahl[d.id]);
                    const name = i18n.tCode("prop", d.definition, d.definition);
                    /* Bei der Farbtemperatur wandert die Einordnung
                       mit: „NW · 4000 K → TL · 5000 K" zeigt beides. */
                    return `${name}: ${alt ? dimensionText(d, alt, i18n, cct) : "—"} → `
                      + `${neu ? dimensionText(d, neu, i18n, cct) : "—"}`;
                  });
                return geaendert.length === 0
                  ? <em>{t("neu.modelNoChange")}</em>
                  : geaendert.join(" · ");
              })()}
            </dd>
            <dt>{t("neu.modelUnchanged")}</dt>
            <dd>
              {modellAchsen
                .filter((d) => modellWahl[d.id] !== undefined
                  && modellWahl[d.id] === (basisVariante?.values.find(
                    (w) => w.dimensionId === d.id)?.valueId ?? null))
                .map((d) => {
                  const v = d.values.find((x) => x.id === modellWahl[d.id]);
                  return `${i18n.tCode("prop", d.definition, d.definition)}: `
                    + (v ? dimensionText(d, v, i18n, cct) : "—");
                }).join(" · ") || "—"}
            </dd>
            <dt>{t("neu.modelSkuOwn")}</dt>
            <dd>{[nummer, bezeichnung].filter((x) => x !== "").join(" · ") || "—"}</dd>
          </dl>
        </div>
      )}

      {/*
        * WAS UEBERNOMMEN WIRD -- vollstaendig, damit die andere Liste
        * glaubwuerdig ist. Wer nur hoert, was NICHT passiert, glaubt es
        * nicht; wer beides sieht, kann entscheiden.
        */}
      <div className="neu__uebernahme">
        <h3 className="neu__nichttitel">{t("neu.inheritTitle")}</h3>
        <ul className="neu__aehnlich">
          <li>{t("neu.inheritFamily")}</li>
          <li>{t("neu.inheritCategory")}</li>
          {ausgang !== null && bezeichnung !== "" && (
            <li>{t("neu.inheritDescription", { number: ausgang.articleNumber })}</li>
          )}
          {modell !== null && modellAchsen.length > 0 && (
            <li>{t("neu.inheritModel", { model: modell.name })}</li>
          )}
        </ul>
      </div>

      <div className="neu__nichtgespeichert">
        <h3 className="neu__nichttitel">{t("neu.notRecordedTitle")}</h3>
        {(ausgang !== null || gewaehlteParameter.length > 0) && (
          <dl className="neu__summeliste">
            {ausgang !== null && (
              <>
                <dt>{t("neu.stepBase")}</dt>
                <dd>{ausgang.articleNumber}</dd>
              </>
            )}
            {gewaehlteParameter.length > 0 && (
              <>
                <dt>{t("neu.stepVariant")}</dt>
                <dd>{gewaehlteParameter.join(" · ")}</dd>
              </>
            )}
          </dl>
        )}
        {/* Namentlich, nicht als Sammelbegriff: „einiges wird nicht
            uebernommen" waere keine Auskunft. */}
        <ul className="neu__aehnlich">
          <li>{modell !== null && modellAchsen.length > 0
            ? t("neu.notPropertiesModel") : t("neu.notProperties")}</li>
          <li>{t("neu.notTexts")}</li>
          <li>{t("neu.notSupplier")}</li>
          <li>{t("neu.notLogistics")}</li>
          <li>{t("neu.notCompliance")}</li>
        </ul>
        <p className="neu__hint">{t("neu.notRecordedHint")}</p>
      </div>

      {/* Was danach offen ist -- und wo es beantwortet wird. */}
      <div className="neu__offen">
        <h3 className="neu__nichttitel">{t("neu.openTitle")}</h3>
        <ul className="neu__aehnlich">
          <li>{t("neu.openLifecycle")}</li>
          <li>{t("neu.openProperties")}</li>
          {neueFamilie && <li>{t("neu.openFamily")}</li>}
        </ul>
      </div>
    </div>
  );
}

/**
 * Die Artikel einer Familie -- als Anschauung fuer Schritt 3.
 *
 * Bewusst ungefiltert nach Betriebszustand: auch ein ausgelaufener Artikel ist
 * ein brauchbares Vorbild fuer eine Nummer, und ihn zu verschweigen liesse
 * eine Nummer doppelt vergeben.
 */
function useFamilienartikel(familyId: string): {
  artikel: readonly ArticleListItem[]; ladeArtikel: boolean;
} {
  const [artikel, setArtikel] = useState<readonly ArticleListItem[]>([]);
  const [ladeArtikel, setLade] = useState(false);
  useEffect(() => {
    if (familyId === "") { setArtikel([]); return; }
    let aktuell = true;
    setLade(true);
    void (async () => {
      try {
        const r = await api.articles({ familyId: Number(familyId), availability: "alle", pageSize: 50 });
        if (aktuell) setArtikel(r.items);
      } catch {
        /* Die Anlage darf an der Anschauung nicht scheitern. Ohne Liste
           bleibt Schritt 3 leer -- und die Nummer vergibt man selbst. */
        if (aktuell) setArtikel([]);
      } finally {
        if (aktuell) setLade(false);
      }
    })();
    return () => { aktuell = false; };
  }, [familyId]);
  return { artikel, ladeArtikel };
}

/**
 * Das Ausgangsprodukt mit seinem Produktmodell.
 *
 * Zwei Abfragen, weil es zwei Wahrheiten sind: die Artikelakte sagt, ob und
 * welchem Modell die SKU zugeordnet ist und welche Auspraegungen sie traegt;
 * das Modell sagt, welche Achsen es ueberhaupt gibt und welche Werte darauf
 * zulaessig sind.
 */
function useBasis(articleId: number | null): {
  basis: ArticleDetail | null; ladeBasis: boolean;
} {
  const [basis, setBasis] = useState<ArticleDetail | null>(null);
  const [ladeBasis, setLade] = useState(false);
  useEffect(() => {
    if (articleId === null) { setBasis(null); return; }
    let aktuell = true;
    setLade(true);
    void (async () => {
      try { const a = await api.article(articleId); if (aktuell) setBasis(a); }
      catch { if (aktuell) setBasis(null); }
      finally { if (aktuell) setLade(false); }
    })();
    return () => { aktuell = false; };
  }, [articleId]);
  return { basis, ladeBasis };
}

/** Die Produktmodelle EINER Familie. Sie sind die technischen Plattformen
 *  dieser fachlichen Gruppe -- nicht die Modelle des ganzen Bestands. */
function useFamilienmodelle(familyId: string): {
  modelle: readonly ProductModelListItem[]; ladeModelle: boolean;
} {
  const [modelle, setModelle] = useState<readonly ProductModelListItem[]>([]);
  const [ladeModelle, setLade] = useState(false);
  useEffect(() => {
    if (!familyId) { setModelle([]); return; }
    let aktuell = true;
    setLade(true);
    void (async () => {
      try { const r = await api.models({ familyId: Number(familyId) });
            if (aktuell) setModelle(r.items); }
      catch { if (aktuell) setModelle([]); }
      finally { if (aktuell) setLade(false); }
    })();
    return () => { aktuell = false; };
  }, [familyId]);
  return { modelle, ladeModelle };
}

/** Das vollstaendige Modell zu einer Kennung -- mit Achsen und Werten. */
function useModellDetail(modelId: number | null): {
  modell: ProductModelDetail | null; ladeModell: boolean;
  setzeModell: (m: ProductModelDetail) => void;
} {
  const [modell, setModell] = useState<ProductModelDetail | null>(null);
  const [ladeModell, setLade] = useState(false);
  useEffect(() => {
    if (modelId === null) { setModell(null); return; }
    let aktuell = true;
    setLade(true);
    void (async () => {
      try { const m = await api.model(modelId); if (aktuell) setModell(m); }
      catch {
        /* Ohne Modellauskunft bleibt der Assistent der von vorher -- er
           zeigt dann den Variantenraum und behauptet keine Modellebene. */
        if (aktuell) setModell(null);
      } finally { if (aktuell) setLade(false); }
    })();
    return () => { aktuell = false; };
  }, [modelId]);
  return { modell, ladeModell, setzeModell: setModell };
}

/**
 * Der Wortlaut eines Dimensionswertes -- dieselbe Darstellung wie am Merkmal.
 *
 * MIT EINER AUSNAHME: der Farbtemperatur.
 *
 * „4000 K" ist technisch richtig und sagt einem Menschen, der das
 * greenius-Sortiment im Kopf hat, weniger als „NW". Umgekehrt waere „NW"
 * allein der Verlust der technischen Wahrheit -- und bei `tl+` sogar
 * mehrdeutig, weil es im Bestand fuer 6000 UND 6500 K steht.
 *
 * Deshalb BEIDES, in dieser Reihenfolge: „NW · 4000 K". Die Einordnung
 * fuehrt, die Zahl belegt. Sie wird ABGELEITET -- aus dem Katalog, nicht
 * aus einer zweiten Auswahl.
 */
function dimensionText(d: ModelDimension, v: {
  num: string | null; num2?: string | null; min: string | null; max: string | null;
  bool: boolean | null; code: string | null; text: string | null;
}, i18n: I18n, cct?: readonly CctValue[]): string {
  /*
   * `30 x 70` sind ZWEI RICHTUNGEN derselben Optik, kein Bereich. Deshalb
   * das Malzeichen und nicht der Gedankenstrich -- `30–70` hiesse „30 bis
   * 70 Grad" und waere eine andere Aussage (Migration 0045).
   */
  const zahl = v.num !== null
    ? (v.num2 ? `${v.num}${unitSuffix(d.unit)} × ${v.num2}` : v.num)
    : (v.min !== null && v.min === v.max ? v.min
      : v.min !== null || v.max !== null ? `${v.min ?? ""}–${v.max ?? ""}` : null);
  if (d.definition === "cct" && v.num !== null) {
    const kelvin = Number(v.num);
    const kat = cct?.find((x) => x.kelvin === kelvin)?.lightColour ?? null;
    const technisch = `${kelvin}${unitSuffix(d.unit)}`;
    /* Ohne Katalogeintrag steht die Zahl allein -- eine Einordnung zu
       erfinden waere schlimmer als keine zu zeigen. */
    /*
     * DAS KUERZEL BLEIBT DAS KUERZEL.
     *
     * `nw` heisst auf Deutsch „Neutralweiss" und auf Englisch „Neutral
     * white" -- beides steht im Woerterbuch und wird an den Merkmalen
     * auch so gezeigt. HIER nicht: WW/NW/TL/TL+ sind die Kuerzel, mit
     * denen im Haus gearbeitet wird, und in einer Auswahl neben dem
     * Kelvinwert ist „NW · 4000 K" kuerzer und eindeutiger als
     * „Neutralweiss · 4000 K". Ein Kuerzel zu uebersetzen, das als
     * Kuerzel benutzt wird, hilft niemandem.
     */
    return kat === null ? technisch : `${kat.toUpperCase()} · ${technisch}`;
  }
  const roh = v.code ?? v.text ?? zahl ?? (v.bool === null ? "" : String(v.bool));
  return `${roh}${unitSuffix(d.unit)}`;
}

/** Der Variantenraum des gewaehlten Ausgangsprodukts. */
function useVariantenraum(articleId: number | null): {
  achsen: readonly VariantDimension[]; ladeAchsen: boolean;
} {
  const [achsen, setAchsen] = useState<readonly VariantDimension[]>([]);
  const [ladeAchsen, setLade] = useState(false);
  useEffect(() => {
    if (articleId === null) { setAchsen([]); return; }
    let aktuell = true;
    setLade(true);
    void (async () => {
      try {
        const r = await api.variantSpace(articleId);
        if (aktuell) setAchsen(r.dimensions);
      } catch {
        if (aktuell) setAchsen([]);
      } finally {
        if (aktuell) setLade(false);
      }
    })();
    return () => { aktuell = false; };
  }, [articleId]);
  return { achsen, ladeAchsen };
}
